/*
  Copyright 2021-2024 New Vector Ltd.
  SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
  Please see LICENSE in the repository root for full details.
  */

import {
  loginWithPasskey,
  passkeyRequest,
  passkeyErrorMessage,
} from "./passkeys";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { Config } from "../config/Config";
import { getProductName } from "../branding";
import { logger } from "matrix-js-sdk/lib/logger";

import {
  type FC,
  type FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@vector-im/compound-web";

import { useClient } from "../ClientContext";
import { FieldRow, InputField, ErrorMessage } from "../input/Input";
import styles from "./LoginPage.module.css";
import { useInteractiveLogin } from "./useInteractiveLogin";
import { prepareJoinSession } from "./joinSession";
import { usePageTitle } from "../usePageTitle";
import { PosthogAnalytics } from "../analytics/PosthogAnalytics";

const getFriendlyError = (error: any, t: TFunction): Error => {
  const status =
    error.httpStatus ||
    (error.message && error.message.match(/\[(\d+)\]/)?.[1]);
  const errcode = error.errcode;

  let message = t(
    "instance.login_page.an_error_occurred_while_connecting_please_try_again_later",
  );

  if (status === 403 || status === "403" || errcode === "M_FORBIDDEN") {
    message = t("instance.login_page.invalid_username_or_password");
  } else if (
    status === 429 ||
    status === "429" ||
    errcode === "M_LIMIT_EXCEEDED"
  ) {
    message = t(
      "instance.login_page.too_many_attempts_please_wait_a_few_minutes",
    );
  } else if (status === 404 || status === "404") {
    message = t("instance.login_page.server_not_found_or_unreachable");
  } else if (error.message) {
    logger.error("Matrix login failed");
  }

  return new Error(message);
};

export const LoginPage: FC = () => {
  const { t } = useTranslation();

  const uiText = {
    pageTitle: t("instance.login_page.pageTitle"),
    title: t("instance.login_page.title"),
    usernameLabel: t("instance.login_page.usernameLabel"),
    passwordLabel: t("instance.login_page.passwordLabel"),
    btnLogin: t("instance.login_page.btnLogin"),
    btnLoading: t("instance.login_page.btnLoading"),
  };

  usePageTitle(uiText.pageTitle);

  const { client, setClient } = useClient();
  const login = useInteractiveLogin(client);
  const homeserver = Config.defaultHomeserverUrl();
  const usernameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error>();
  const [passkeyEnabled, setPasskeyEnabled] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void passkeyRequest("/capabilities")
      .then((data) => {
        if (!cancelled)
          setPasskeyEnabled(!!data.enabled && !!window.PublicKeyCredential);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const onSubmitLoginForm = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setLoading(true);
      setError(undefined);

      if (!homeserver || !usernameRef.current || !passwordRef.current) {
        setError(Error(t("instance.ui.login_parameters_missing")));
        setLoading(false);
        return;
      }

      const enteredPassword = passwordRef.current.value;
      login(homeserver, usernameRef.current.value, enteredPassword)
        .then(async ([client, session]) => {
          if (!setClient) {
            return;
          }

          try {
            await prepareJoinSession(client, enteredPassword);
          } catch (error) {
            await client.logout(true).catch(() => {});
            throw error;
          }
          setClient(client, session);
          if (passwordRef.current) passwordRef.current.value = "";

          const locationState = location.state;
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          if (locationState && locationState.from) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            await navigate(locationState.from);
          } else {
            await navigate("/");
          }
          PosthogAnalytics.instance.eventLogin.track();
        })
        .catch((err) => {
          setError(getFriendlyError(err, t));
          setLoading(false);
        })
        .finally(() => {
          if (passwordRef.current) passwordRef.current.value = "";
        });
    },
    [login, location, navigate, homeserver, setClient, t],
  );

  return (
    <>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.formContainer}>
            <h1
              style={{
                color: "var(--cpd-color-text-primary)",
                fontSize: "28px",
                margin: "0 0 16px 0",
                textAlign: "center",
              }}
            >
              {getProductName()}
            </h1>

            <h2>{uiText.title}</h2>
            <form onSubmit={onSubmitLoginForm}>
              <FieldRow>
                <InputField
                  type="text"
                  ref={usernameRef}
                  placeholder={uiText.usernameLabel}
                  label={uiText.usernameLabel}
                  autoCorrect="off"
                  autoCapitalize="none"
                  data-testid="login_username"
                />
              </FieldRow>
              <FieldRow>
                <InputField
                  type="password"
                  ref={passwordRef}
                  placeholder={uiText.passwordLabel}
                  label={uiText.passwordLabel}
                  data-testid="login_password"
                />
              </FieldRow>
              {error && (
                <FieldRow>
                  <ErrorMessage error={error} />
                </FieldRow>
              )}
              <FieldRow>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="login_login"
                >
                  {loading ? uiText.btnLoading : uiText.btnLogin}
                </Button>
              </FieldRow>
              {passkeyEnabled && (
                <FieldRow>
                  <Button
                    type="button"
                    kind="secondary"
                    disabled={loading}
                    onClick={() => {
                      if (!setClient) return;
                      setLoading(true);
                      setError(undefined);
                      void loginWithPasskey(client)
                        .then(async ([next, session]) => {
                          setClient(next, session);
                          const from = (location.state as { from?: string })
                            ?.from;
                          await navigate(from || "/");
                        })
                        .catch((cause) =>
                          setError(new Error(passkeyErrorMessage(cause, t))),
                        )
                        .finally(() => setLoading(false));
                    }}
                  >
                    {t("instance.ui.login_passkey")}
                  </Button>
                </FieldRow>
              )}
            </form>
          </div>
        </div>
      </div>
    </>
  );
};
